import React, { useMemo, useState } from "react";
import { Box, Button, IconButton, Tooltip, Typography } from "@material-ui/core";
import { Add, Delete, DragIndicator, Visibility } from "@material-ui/icons";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useSortableFieldArray } from "routes/createContent/views/lessons/hooks/useSortableFieldArray";
import { SortableList } from "shared/SortableList";
import { TemplateFormLesson } from "../types";
import LessonPickerDialog from "./LessonPickerDialog";
import LessonPreviewDialog from "./LessonPreviewDialog";
import TemplateItemRow from "./TemplateItemRow";

type RowProps = {
  id: string;
  title: string;
  imageUrl: string | null;
  readOnly?: boolean;
  onPreview: () => void;
  onRemove: () => void;
};

// A single full-width lesson row, styled to match the exercise rows (thumbnail,
// title, aligned actions, full-width delimiter). Uses useSortable directly so
// the drag handle sits inline with the other actions.
const SortableLessonRow: React.VFC<RowProps> = ({ id, title, imageUrl, onPreview, onRemove, readOnly = false }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = useMemo<React.CSSProperties>(
    () => ({ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }),
    [transform, transition, isDragging]
  );

  return (
    <div ref={setNodeRef} style={style}>
      <TemplateItemRow imageUrl={imageUrl} title={title}>
        <Box display="flex" alignItems="center" style={{ flexShrink: 0 }}>
          <Tooltip title="Preview">
            <IconButton size="small" aria-label={`Preview ${title}`} onClick={onPreview}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          {!readOnly && (
            <>
              <Tooltip title="Remove">
                <IconButton size="small" aria-label={`Remove ${title}`} onClick={onRemove}>
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton
                size="small"
                aria-label={`Reorder ${title}`}
                {...attributes}
                {...listeners}
                style={{ cursor: "grab" }}
              >
                <DragIndicator fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>
      </TemplateItemRow>
    </div>
  );
};

const LessonsSection: React.VFC<{ readOnly?: boolean }> = ({ readOnly = false }) => {
  const { fields, ids, onDragEnd, append, remove } = useSortableFieldArray("lessons");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [previewId, setPreviewId] = useState<number | null>(null);

  const existingIds = fields.map(f => f.text_lesson_id as number);

  const onAdd = (lessons: TemplateFormLesson[]) => lessons.forEach(ls => append(ls));

  return (
    <Box mt={3}>
      <Box display="flex" alignItems="center" mb={1}>
        <Typography variant="h6">Lessons</Typography>
        {!readOnly && (
          <Button startIcon={<Add />} size="small" style={{ marginLeft: 12 }} onClick={() => setPickerOpen(true)}>
            Add lessons
          </Button>
        )}
      </Box>

      {fields.length === 0 ? (
        <Typography color="textSecondary">No lessons added yet.</Typography>
      ) : (
        <SortableList items={ids} onDragEnd={onDragEnd}>
          {fields.map((f, i) => (
            <SortableLessonRow
              key={ids[i]}
              id={ids[i]}
              title={(f.title as string) || `Lesson ${i + 1}`}
              imageUrl={f.image_url as string | null}
              readOnly={readOnly}
              onPreview={() => setPreviewId(f.text_lesson_id as number)}
              onRemove={() => remove(i)}
            />
          ))}
        </SortableList>
      )}

      <LessonPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        existingIds={existingIds}
        onAdd={onAdd}
      />
      <LessonPreviewDialog lessonId={previewId} onClose={() => setPreviewId(null)} />
    </Box>
  );
};

export default LessonsSection;
